import { Grid, Alert, Box } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useBalance } from './useBalance';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    FONT_WEIGHT,
    Heading,
    LINE_HEIGHT,
    TEXT_SIZE,
    TextMDSemiBold
} from '@/typography/Typography';
import Chip from '@/components/shared/Chip';
import { AvailableBalance } from './balance/AvailableBalance';
import { MileageOverview } from './balance/MileageOverview';
import { BalanceSummary } from './balance/BalanceSummary';
import { ViewSwitch } from '@/components/shared/ViewSwitch';
import { ShiftListType, ShiftsTable } from './shifts/ShiftsTable';
import { useShifts } from './useShifts';

import { useAppDispatch, useCompany, usePayments, useUsers } from '@/lib/hooks';
import {
    getCurrentPaymentCompany,
    getUserTransactionsHistory,
    setAiiaRetryErrorHandled
} from '@/lib/slices/paymentsSlice';
import { BalanceSummaryDetailsDialog } from './balance/BalanceSummaryDetailsDialog';
import {
    PrimaryButton,
    PrimaryInvertedButton
} from '@/components/shared/Button';
import { useUsersMutations } from '@/services/usersMutations';
import { useTranslation } from 'react-i18next';
import { UpdateUserForm } from './update/UpdateUserForm';
import { UpdateBankAccountForm } from './bankAccountDetails/UpdateBankAccountForm';
import { GenerateOtpDialog } from './otp/GenerateOtpDialog';
import { getUserData, sendActivationEmail } from '@/lib/slices/usersSlice';
import { FETCH_STATE } from '@/lib/slices/types';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useFormatting } from '@/utils/formatting';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { UserTransactions } from './UserTransactions';
import { UserContext, useUser } from '@/contexts/user/UserContext';
import { useBalanceV2, useWithdrawalValidation } from '@instapaid/shared-logic';
import { AvailableShiftsTable } from './shifts/AvailableShiftsTable';
import { DATE_FORMAT } from '@/lib/constants';
import {
    getUserTips,
    useCurrentCompanyDetails,
    useGetUserQuery,
    useGetPayrollStatus,
    useGetUpcomingShifts,
    useGetAvailableShifts
} from '@/services/paymentsQuery';
import {
    AggregatedAlerts,
    AlertList
} from '@/components/shared/AggregatedAlerts';
import { UserRoles } from '@/models/company';
import { useEarningPermission } from '@/lib/hooks/useEarningPermission';
import { TipsOverview } from './balance/TipsOverview';
import { SecondaryDataSourceWarning } from '@/components/shared/SecondaryDataSourceWarning';
import { getSecondaryProviderName } from '@/utils/providers';
import { TipsTable } from './tips/TipsTable';

const adminRoles = [
    UserRoles.Admin,
    UserRoles.SuperAdmin,
    UserRoles.GroupAdmin
];

const rolesAllowedToViewShifts = adminRoles;

const rolesAllowedToViewBalance = adminRoles;

export const UserDetailsPage = () => {
    let { userId } = useParams();

    const { user } = useUser();
    const { data: companyData } = useCurrentCompanyDetails();
    const { userData } = useUsers();

    const { accessToUserEarningsAllowed } = useEarningPermission(user.role);

    const {
        balance,
        isLoading: isBalanceLoading,
        taxCardError
    } = useBalance(userId!);

    const { t } = useTranslation();

    const {
        shifts: doneShifts,
        shiftsError,
        totalHours: doneHours,
        totalEarnings: doneEarnings,
        isLoading: isShiftLoading
    } = useShifts(userId!, balance?.details?.employmentType);

    const { data: tipsDate } = getUserTips(userId!);

    const isExcludedFromTips = tipsDate?.data?.excluded ?? false;

    const dispatch = useAppDispatch();

    const { formatDate, getBalanceLabel, toDecimal } = useFormatting();

    const payrollPeriodStr = balance?.details?.payrollPeriodStart
        ? formatDate(
              balance.details.payrollPeriodStart,
              DATE_FORMAT.DATE_MONTH_YEAR,
              true
          ) +
          ' - ' +
          formatDate(
              balance.details.payrollPeriodEnd,
              DATE_FORMAT.DATE_MONTH_YEAR,
              true
          )
        : 'Not set';

    const {
        userTransactionHistory,
        paymentCompany,
        aiiaRefreshPayment,
        aiiaRetryAuthorize
    } = usePayments();

    const { data: upcomingShiftsData, isLoading: isUpcomingShiftsLoading } =
        useGetUpcomingShifts(
            userId!,
            balance?.details?.employmentType === 'Hourly'
        );

    const isHourlyEmployee = balance?.details?.employmentType === 'Hourly';
    const shouldFetchAvailableShifts =
        isHourlyEmployee ||
        (!isHourlyEmployee &&
            !!balance?.details?.employmentType &&
            !!companyData?.data?.enableShiftsForFixedEmployees);

    const { data: availableShiftsData, isLoading: isAvailableShiftsLoading } =
        useGetAvailableShifts(userId!, shouldFetchAvailableShifts);
    const { setAlert } = useNotificationsSnackbar();

    useEffect(() => {
        if (aiiaRefreshPayment.status == FETCH_STATE.SUCCEEDED) {
            setAlert(t('status_refreshed'), 'success');
        } else if (aiiaRefreshPayment.status == FETCH_STATE.FAILED) {
            setAlert(t('status_refresh_failed'), 'error');
        }
    }, [aiiaRefreshPayment.status]);

    useEffect(() => {
        if (aiiaRetryAuthorize.status == FETCH_STATE.SUCCEEDED) {
            setAlert(t('success'), 'success');
        } else if (aiiaRetryAuthorize.status == FETCH_STATE.FAILED) {
            setAlert(t('transaction_retry_failed'), 'error');
            dispatch(setAiiaRetryErrorHandled());
        }
    }, [aiiaRetryAuthorize.status]);

    const currentPeriodShifts = upcomingShiftsData;

    useEffect(() => {
        dispatch(
            setLoader(
                userData.status == FETCH_STATE.LOADING ||
                    aiiaRefreshPayment.status == FETCH_STATE.LOADING ||
                    aiiaRetryAuthorize.status == FETCH_STATE.LOADING ||
                    isUpcomingShiftsLoading ||
                    isAvailableShiftsLoading ||
                    paymentCompany.status == FETCH_STATE.LOADING ||
                    isShiftLoading ||
                    isBalanceLoading
            )
        );
    }, [
        userData.status,
        aiiaRefreshPayment.status,
        aiiaRetryAuthorize.status,
        isUpcomingShiftsLoading,
        isAvailableShiftsLoading,
        paymentCompany.status,
        isShiftLoading,
        isBalanceLoading
    ]);

    useEffect(() => {
        if (shiftsError) {
            setAlert('Could not fetch shifts for user', 'error');
        }
    }, [shiftsError]);

    const [tabIndex, setTabIndex] = useState<number | null>(null);

    const availableShiftsArr = availableShiftsData?.shifts;

    const { awaitingAndAvailableHours } = useBalanceV2(balance?.details!);

    useEffect(() => {
        dispatch(getUserData(userId!));
        dispatch(getCurrentPaymentCompany());
        dispatch(
            getUserTransactionsHistory({ userId: userId!, limit: 7, offset: 0 })
        );
    }, [userId]);

    const payrollStartStr = formatDate(
        new Date(balance?.details?.payrollPeriodStart ?? ''),
        undefined,
        true
    );

    const payrollEndStr = formatDate(
        new Date(balance?.details?.payrollPeriodEnd ?? ''),
        undefined,
        true
    );

    const nextPayrollStartStr = formatDate(dayjs().toDate());

    const nextPayrollEndStr = formatDate(dayjs().add(30, 'days').toDate());

    const navigate = useNavigate();

    const viewSwitchItems = useMemo(() => {
        const hourlyEmployeeItems = () => {
            let items = [];

            if (balance?.details?.employmentType === 'Hourly') {
                if (doneShifts) {
                    items.push({
                        primaryText: 'Worked shifts',
                        badgeText: doneShifts?.length.toString(),
                        route: 'balance',
                        routeComponent: (
                            <ShiftsTable
                                heading={t('payroll_period_text', {
                                    startDate: payrollStartStr,
                                    endDate: payrollEndStr
                                })}
                                shiftListType={ShiftListType.WORKED}
                                shifts={doneShifts}
                                userId={userId!}
                                payrollPeriod={payrollPeriodStr}
                                shiftStatusWithdrawAllowed={
                                    balance?.details?.shiftStatusWithdrawAllowed
                                }
                                totalHours={doneHours}
                                totalEarnings={doneEarnings}
                                payoutType={
                                    paymentCompany?.company
                                        ?.shiftStatusWithdrawAllowed ?? ''
                                }
                                companyName={companyData?.data?.name ?? ''}
                            />
                        )
                    });
                }

                if (currentPeriodShifts) {
                    items.push({
                        primaryText: 'Upcoming shifts',
                        badgeText:
                            currentPeriodShifts?.shifts.length.toString() ??
                            '0',
                        route: 'upcoming-shifts',
                        routeComponent: (
                            <ShiftsTable
                                heading={t('payroll_period_text', {
                                    startDate: payrollStartStr,
                                    endDate: payrollEndStr
                                })}
                                shifts={currentPeriodShifts?.shifts}
                                userId={userId!}
                                payrollPeriod={payrollPeriodStr}
                                shiftListType={ShiftListType.UPCOMING}
                                shiftStatusWithdrawAllowed={
                                    balance?.details?.shiftStatusWithdrawAllowed
                                }
                                totalEarnings={currentPeriodShifts?.totalAmount}
                                totalHours={currentPeriodShifts?.totalHours}
                                payoutType={
                                    paymentCompany?.company
                                        ?.shiftStatusWithdrawAllowed ?? ''
                                }
                                companyName={companyData?.data?.name ?? ''}
                            />
                        )
                    });
                }

                if (availableShiftsArr) {
                    items.push({
                        primaryText: 'Available shifts',
                        badgeText:
                            availableShiftsArr?.length?.toString() ?? '0',
                        route: 'available-shifts',
                        routeComponent: (
                            <AvailableShiftsTable
                                heading={t('next_30_days', {
                                    startDate: nextPayrollStartStr,
                                    endDate: nextPayrollEndStr
                                })}
                                shifts={availableShiftsArr}
                                userId={userId!}
                                payrollPeriod={payrollPeriodStr}
                                shiftStatusWithdrawAllowed={
                                    balance?.details?.shiftStatusWithdrawAllowed
                                }
                                totalEarnings={availableShiftsData?.totalAmount}
                                totalHours={availableShiftsData?.totalHours}
                                payoutType={
                                    paymentCompany?.company
                                        ?.shiftStatusWithdrawAllowed ?? ''
                                }
                                companyName={companyData?.data?.name ?? ''}
                            />
                        )
                    });
                }
            }

            return items;
        };

        const allEmployeeItems = () => {
            const employeeItems = [];

            if (
                balance?.details?.employmentType !== 'Hourly' &&
                companyData?.data?.enableShiftsForFixedEmployees &&
                availableShiftsArr
            ) {
                employeeItems.push({
                    primaryText: 'Available shifts',
                    badgeText: availableShiftsArr?.length?.toString() ?? '0',
                    route: 'available-shifts',
                    routeComponent: (
                        <AvailableShiftsTable
                            heading={t('next_30_days', {
                                startDate: nextPayrollStartStr,
                                endDate: nextPayrollEndStr
                            })}
                            shifts={availableShiftsArr}
                            userId={userId!}
                            payrollPeriod={payrollPeriodStr}
                            shiftStatusWithdrawAllowed={
                                balance?.details?.shiftStatusWithdrawAllowed
                            }
                            totalEarnings={availableShiftsData?.totalAmount}
                            totalHours={availableShiftsData?.totalHours}
                            payoutType={
                                paymentCompany?.company
                                    ?.shiftStatusWithdrawAllowed ?? ''
                            }
                            companyName={companyData?.data?.name ?? ''}
                        />
                    )
                });
            }

            if (tipsDate?.data?.tips) {
                employeeItems.push({
                    primaryText: 'Tips',
                    badgeText: tipsDate?.data?.tips?.length?.toString() ?? '0',
                    route: 'tips',
                    routeComponent: (
                        <TipsTable
                            heading="Tips"
                            total={tipsDate?.data?.totalAmount ?? 0}
                            totalHours={tipsDate?.data?.totalHours ?? 0}
                            tips={tipsDate?.data?.tips}
                            userId={userId!}
                            payrollPeriod={payrollPeriodStr}
                            payoutType={
                                paymentCompany?.company
                                    ?.shiftStatusTipsWithdrawAllowed ?? ''
                            }
                            companyName={companyData?.data?.name ?? ''}
                            excluded={isExcludedFromTips}
                            payrollPeriodStart={
                                tipsDate?.data?.payrollPeriodStart
                            }
                            payrollPeriodEnd={tipsDate?.data?.payrollPeriodEnd}
                        />
                    )
                });
            }

            if (userTransactionHistory) {
                employeeItems.push({
                    primaryText: 'Transaction history',
                    badgeText:
                        userTransactionHistory.transactions?.total?.toString() ??
                        '0',
                    route: `user-transactions/page/0`,
                    path: 'user-transactions/page/:pageNumber',
                    overrideTestPath: 'user-transactions/page/',
                    routeComponent: <UserTransactions userId={userId!} />
                });
            }

            return employeeItems;
        };

        return [...hourlyEmployeeItems(), ...allEmployeeItems()];
    }, [
        userId,
        balance?.details?.employmentType,
        balance?.details?.shiftStatusWithdrawAllowed,
        balance?.details?.shiftStatusTipsWithdrawAllowed,
        doneShifts,
        currentPeriodShifts,
        availableShiftsArr,
        availableShiftsData,
        companyData?.data?.enableShiftsForFixedEmployees,
        userTransactionHistory,
        payrollPeriodStr,
        isExcludedFromTips
    ]);

    const [showBalanceDetails, setShowBalanceDetails] =
        useState<boolean>(false);
    const [updateUserDialogOpen, setUpdateUserDialogOpen] =
        useState<boolean>(false);
    const [
        updateBankAccountUserDialogOpen,
        setUpdateBankAccountUserDialogOpen
    ] = useState<boolean>(false);
    const [otpDialogOpen, setOtpDialogOpen] = useState<boolean>(false);
    const [generatedOtp, setGeneratedOtp] = useState<{
        email: string;
        otpCode: string;
    } | null>(null);

    const { generateManualOtp } = useUsersMutations();

    const handleShowDetailsClick = () => {
        setShowBalanceDetails(true);
    };

    const handleShowDetailsCloseClick = () => {
        setShowBalanceDetails(false);
    };

    const openUpdateUserDialog = () => {
        setUpdateUserDialogOpen(true);
    };
    const openUpdateBankAccountUserDialog = () => {
        setUpdateBankAccountUserDialogOpen(true);
    };

    const handleSendActivationEmail = () => {
        dispatch(sendActivationEmail(userId!));
    };

    const handleGenerateOtp = () => {
        generateManualOtp.mutate(userId!, {
            onSuccess: (response) => {
                setGeneratedOtp(response.data);
                setOtpDialogOpen(true);
            },
            onError: () => {
                setAlert('Failed to generate OTP', 'error');
            }
        });
    };

    const { sendActivationMail } = useUsers();

    useEffect(() => {
        dispatch(setLoader(sendActivationMail.status === FETCH_STATE.LOADING));
        if (sendActivationMail.error) {
            setAlert('failed', 'error');
        }
        if (sendActivationMail.status === FETCH_STATE.SUCCEEDED) {
            setAlert('Activation email sent', 'success');
        }
    }, [sendActivationMail.status, sendActivationMail.error]);

    useEffect(() => {
        if (balance?.details?.employmentType == 'Fixed') {
            navigate('./user-transactions/page/0', { replace: true });
        }
    }, [balance?.details?.employmentType]);

    // const { company: companyData } = useCompany();

    const { company } = useContext(UserContext);

    const { data, isPending: userPending } = useGetUserQuery({
        userId: userId,
        enabled: true
    });

    const isFullTimeEmployee = balance?.details?.employmentType === 'Fixed';
    const { data: payrollStatus } = useGetPayrollStatus(
        userId!,
        isFullTimeEmployee
    );

    const {
        bankDetailsEmpty,
        alreadyProcessingTransactions,
        currentMonthPayrollExists,
        isNemkonto,
        isNemkontoMissingCpr
    } = useWithdrawalValidation(
        companyData?.data as any,
        balance?.details,
        {
            bankCode: data?.data?.bankCode,
            accountNumber: data?.data?.bankAccountNumber,
            cprNumber: data?.data?.cprNumber,
            isNemkonto: data?.data?.isNemkonto,
            hasBankDetails: data?.data?.hasBankDetails ?? false
        },
        balance?.details?.pendingWithdrawalsCount,
        payrollStatus?.payrollProcessed
    );

    const [alertList, setAlertList] = useState<AlertList[]>([]);

    useEffect(() => {
        let alerts: AlertList[] = [];

        if (bankDetailsEmpty) {
            alerts.push({
                alertId: 1,
                severity: 'warning',
                message: t('cannot_withdraw_because_bank_details_not_available')
            });
        }

        if (alreadyProcessingTransactions) {
            alerts.push({
                alertId: 2,
                severity: 'warning',
                message: t('pending_approval_cannot_withdraw')
            });
        }

        if (currentMonthPayrollExists) {
            alerts.push({
                alertId: 3,
                severity: 'warning',
                message: t('cannot_withdraw_payslip_exists')
            });
        }
        if (isNemkontoMissingCpr) {
            alerts.push({
                alertId: 5,
                severity: 'error',
                message: (
                    <Box
                        sx={{
                            color: '#D92D20',
                            fontSize: '14px',
                            lineHeight: '20px'
                        }}
                    >
                        <strong>NemKonto</strong> {t('nemkonto_missing_cpr')}
                    </Box>
                )
            });
        }

        if (taxCardError) {
            alerts.push({
                alertId: 4,
                severity: 'error',
                message: t('user_missing_tax_card')
            });
        }

        if (!accessToUserEarningsAllowed(userData?.data?.role)) {
            let earningsAlert: AlertList = {
                alertId: -2,
                severity: 'error',
                message: t('earnings_access_alert')
            };
            alerts = [];
            alerts.push(earningsAlert);
        }

        setAlertList(alerts);
    }, [
        user?.role,
        bankDetailsEmpty,
        isNemkontoMissingCpr,
        alreadyProcessingTransactions,
        currentMonthPayrollExists,
        taxCardError,
        userData?.data?.role,
        accessToUserEarningsAllowed
    ]);

    // Determine if all required data for ViewSwitch has loaded
    const isViewSwitchDataReady = useMemo(() => {
        // Always wait for balance data
        if (isBalanceLoading || !balance) return false;

        // For hourly employees, wait for shifts data
        if (balance?.details?.employmentType === 'Hourly') {
            if (isShiftLoading || isUpcomingShiftsLoading) {
                return false;
            }
        }

        // Wait for available shifts for all employee types
        if (isAvailableShiftsLoading) {
            return false;
        }

        // Wait for transaction history
        if (!userTransactionHistory) return false;

        // Wait for user data
        if (userPending) return false;

        return true;
    }, [
        isBalanceLoading,
        balance,
        isShiftLoading,
        isUpcomingShiftsLoading,
        isAvailableShiftsLoading,
        userTransactionHistory,
        userPending
    ]);

    return (
        <>
            {userData.status == FETCH_STATE.SUCCEEDED && (
                <>
                    <Grid container md={12}>
                        <Grid
                            md={12}
                            container
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            mb={3}
                            ml={1}
                        >
                            <Grid
                                container
                                md={6}
                                direction={'row'}
                                gap={1}
                                alignItems={'center'}
                            >
                                <Box mt={1} ml={1}>
                                    <Link to={-1 as any}>
                                        <ArrowBackIosIcon />
                                    </Link>
                                </Box>

                                <Heading
                                    fontSize={TEXT_SIZE.XLARGE}
                                    fontWeight={FONT_WEIGHT.MEDIUM}
                                    lineHeight={LINE_HEIGHT.XLARGE}
                                >
                                    {userData.data?.firstName}{' '}
                                    {userData.data?.lastName}
                                </Heading>
                                <SecondaryDataSourceWarning
                                    existsInSecondaryDataSource={
                                        userData.data
                                            ?.existsInSecondaryDataSource
                                    }
                                    provider={getSecondaryProviderName(
                                        companyData?.data
                                    )}
                                    variant="chip"
                                />
                                {rolesAllowedToViewBalance.includes(
                                    user?.role
                                ) && (
                                    <Chip
                                        label={
                                            <TextMDSemiBold>
                                                {payrollPeriodStr}
                                            </TextMDSemiBold>
                                        }
                                    />
                                )}
                            </Grid>

                            <Grid
                                container
                                gap={1}
                                item
                                md={5}
                                alignItems={'end'}
                                justifyContent={'flex-end'}
                                mr={1.5}
                            >
                                <PrimaryInvertedButton
                                    onClick={handleSendActivationEmail}
                                >
                                    {t('send_activation_code')}
                                </PrimaryInvertedButton>
                                {user.role === UserRoles.SuperAdmin && (
                                    <PrimaryInvertedButton
                                        onClick={handleGenerateOtp}
                                        loading={generateManualOtp.isPending}
                                    >
                                        Generate OTP
                                    </PrimaryInvertedButton>
                                )}
                                <PrimaryButton onClick={openUpdateUserDialog}>
                                    {t('edit_personal_info')}
                                </PrimaryButton>
                                <PrimaryButton
                                    onClick={openUpdateBankAccountUserDialog}
                                >
                                    {data &&
                                    data.data?.payoutDestinationDisplay ==
                                        null &&
                                    !isNemkonto
                                        ? t('edit_bank')
                                        : t('view_bank')}
                                </PrimaryButton>
                            </Grid>

                            {/* <Link to={-1 as any} style={{ marginRight: '20px' }}>
                    <CloseIcon />
                </Link> */}
                        </Grid>
                        <Box mx={1} display={'flex'} width={'100%'}>
                            {!isBalanceLoading &&
                                !isShiftLoading &&
                                !userPending && (
                                    <AggregatedAlerts alertList={alertList} />
                                )}
                        </Box>
                        {balance && balance.error && (
                            <Alert
                                sx={{ marginTop: '1rem', width: '100%' }}
                                severity="error"
                            >
                                Could not calculate balance for this employee.
                                Received error &quot;{balance.error}&quot;
                            </Alert>
                        )}

                        {balance && balance.error === null && (
                            <Grid container md={12}>
                                {accessToUserEarningsAllowed(
                                    userData?.data?.role
                                ) && (
                                    <Grid md={12} container>
                                        <Grid md={4}>
                                            <AvailableBalance
                                                balance={balance?.balance}
                                                earnings={
                                                    balance?.details?.earned
                                                }
                                                allowancePercentage={
                                                    balance?.details
                                                        ?.allowancePercentage
                                                }
                                            />
                                        </Grid>
                                        {(balance?.details?.mileageTotal > 0 ||
                                            balance?.details?.mileageKm >
                                                0) && (
                                            <Grid md={4}>
                                                <MileageOverview
                                                    mileageTotal={
                                                        balance?.details
                                                            ?.mileageTotal
                                                    }
                                                    mileageKm={
                                                        balance?.details
                                                            ?.mileageKm
                                                    }
                                                />
                                            </Grid>
                                        )}

                                        <Grid md={4}>
                                            <TipsOverview
                                                tipsTotal={
                                                    tipsDate?.data
                                                        ?.totalAmount ?? 0
                                                }
                                                earnedLastShift={
                                                    tipsDate?.data?.tips &&
                                                    tipsDate?.data?.tips
                                                        .length > 0
                                                        ? tipsDate.data.tips.sort(
                                                              (a, b) =>
                                                                  (b?.endDateTime
                                                                      ?.toDate()
                                                                      ?.getTime() ??
                                                                      0) -
                                                                  (a?.endDateTime
                                                                      ?.toDate()
                                                                      ?.getTime() ??
                                                                      0)
                                                          )[0].amount ?? 0
                                                        : 0
                                                }
                                                excluded={isExcludedFromTips}
                                            />
                                        </Grid>

                                        <Grid md={4}>
                                            <BalanceSummary
                                                onShowAllDetails={
                                                    handleShowDetailsClick
                                                }
                                                taxRate={
                                                    balance?.details
                                                        .calculationsDetails
                                                        ?.taxRate
                                                }
                                                taxDeduction={
                                                    balance?.details
                                                        .calculationsDetails
                                                        ?.taxDeduction
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>

                    {user.role == UserRoles.SuperAdmin && (
                        <>
                            {' '}
                            <Box
                                pt={2}
                                pl={1}
                                flexDirection={'row'}
                                gap={2}
                                display={'flex'}
                            >
                                <Chip
                                    label={
                                        'Available Hours: ' +
                                        toDecimal(
                                            awaitingAndAvailableHours?.availableHours,
                                            2
                                        )
                                    }
                                    variant="outlined"
                                />
                                <Chip
                                    label={
                                        'Awaiting Hours: ' +
                                        toDecimal(
                                            awaitingAndAvailableHours?.awaitingHours,
                                            2
                                        )
                                    }
                                    variant="outlined"
                                />
                                <Chip
                                    label={
                                        'Upcoming Shifts Total: ' +
                                        toDecimal(
                                            balance?.details
                                                ?.upcomingShiftsTotalHours,
                                            2
                                        ) +
                                        ' hours upto ' +
                                        getBalanceLabel(
                                            balance?.details
                                                ?.upcomingShiftsTotalAmount,
                                            company?.currency
                                        )
                                    }
                                    variant="outlined"
                                />

                                <Chip
                                    label={
                                        'Available Shifts Total Count: ' +
                                        toDecimal(
                                            balance?.details
                                                ?.availableShiftsTotalCount,
                                            2
                                        ) +
                                        ' shifts upto ' +
                                        getBalanceLabel(
                                            balance?.details
                                                ?.availableShiftsTotalAmount,
                                            company?.currency
                                        )
                                    }
                                    variant="outlined"
                                />

                                {isNemkonto && (
                                    <Chip
                                        label={
                                            isNemkontoMissingCpr
                                                ? 'NemKonto - Missing CPR'
                                                : 'NemKonto'
                                        }
                                        {...(!isNemkontoMissingCpr
                                            ? { variant: 'outlined' }
                                            : { color: 'error' })}
                                    />
                                )}
                            </Box>
                        </>
                    )}

                    <Box mt={4} />
                    {accessToUserEarningsAllowed(userData?.data?.role) &&
                        isViewSwitchDataReady && (
                            <ViewSwitch
                                itemList={viewSwitchItems}
                                setSelectedIndex={setTabIndex}
                                selectedIndex={tabIndex}
                            />
                        )}

                    <BalanceSummaryDetailsDialog
                        payrollDateString={payrollPeriodStr}
                        balance={balance}
                        onClose={handleShowDetailsCloseClick}
                        open={showBalanceDetails}
                    />
                    <UpdateUserForm
                        onUserListChange={() => {}}
                        userId={userId}
                        open={updateUserDialogOpen}
                        setOpen={(open: boolean) =>
                            setUpdateUserDialogOpen(open)
                        }
                    />
                    <UpdateBankAccountForm
                        onUserListChange={() => {}}
                        userId={userId}
                        open={updateBankAccountUserDialogOpen}
                        setOpen={(open: boolean) =>
                            setUpdateBankAccountUserDialogOpen(open)
                        }
                    />
                    <GenerateOtpDialog
                        open={otpDialogOpen}
                        onClose={() => setOtpDialogOpen(false)}
                        email={generatedOtp?.email}
                        otpCode={generatedOtp?.otpCode}
                    />
                </>
            )}
        </>
    );
};
